import { formatPrice } from '@general/helpers';
import '../../snippets/product-tags/product-tags.scss';

/**
 * Retrieves a cookie value by name
 *
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string|null} The cookie value or null if not found
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

/**
 * Sets a cookie with specified name, value and expiration days
 *
 * @param {string} name - The name of the cookie
 * @param {string} value - The value to store
 * @param {number} days - Number of days until expiration
 */
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  let expires = 'expires=' + d.toUTCString();
  document.cookie = name + '=' + value + ';' + expires + ';path=/';
}

/**
 * Adds a product handle to the recently viewed cookie
 *
 * @param {string} productHandle - The Shopify product handle to add
 */
function addProductToRecentlyViewed(productHandle) {
  let recentlyViewed = getCookie('recently_viewed') ? JSON.parse(getCookie('recently_viewed')) : [];

  if (!recentlyViewed.includes(productHandle)) {
    recentlyViewed.push(productHandle);
  }

  let recentlyLimit = window.dAgency?.recentlyLimit || 8;

  if (recentlyViewed.length > recentlyLimit) {
    recentlyViewed.shift();
  }

  setCookie('recently_viewed', JSON.stringify(recentlyViewed), 30);
}

/**
 * Displays recently viewed products in a carousel
 * Fetches product data and renders product tiles dynamically
 */
function displayRecentlyViewed() {
  const recentlyViewed = getCookie('recently_viewed') ? JSON.parse(getCookie('recently_viewed')) : [];

  const section = document.querySelector('.recently-viewed__section');

  // Check if section exists before proceeding
  if (section) {
    if (recentlyViewed.length > 0) {
      section.style.display = 'block';
      const container = document.querySelector(
        '.recently-viewed__container .recently-viewed__list-container .carousel__slider-wrapper .swiper-wrapper',
      );
      container.innerHTML = '';

      recentlyViewed.forEach((handle) => {
        fetch(`/products/${handle}.js`)
          .then((response) => response.json())
          .then((product) => {
            const firstVariant = product.variants[0];

            const comparePrice = formatPrice({
              value: firstVariant.compare_at_price,
            });

            const price = formatPrice({
              value: firstVariant.price,
            });

            const productImages =
              product.images.length > 0 ? product.images : [window.dAgency.__dBlank.mediaPlaceholder];

            const mediaHTML = productImages
              .map(
                (image) => `
                  <div class="swiper-slide">
                    <a href="${product.url}" class="product-tile__gallery-link">
                      <div class="media__container">
                        <img srcset="${image} 800w"
                            src="${image}"
                            alt="${product.title}"
                            class="media product__image"
                            width="800"
                            height="600"
                            loading="lazy">
                      </div>
                    </a>
                  </div>
                `,
              )
              .join('');

            // Wishlist button
            const wishlistHTML = `
            <div class='product-tile__wishlist' data-wishlist-btn data-wishlist-status data-wishlist-blocking
              data-wishlist-variant-id='${firstVariant.id}'
              data-wishlist-product-id='${product.id}'
              data-wishlist-product-url='${window.location.origin}${product.url}'
              data-wishlist-price='${(firstVariant.price / 100).toFixed(2)}'
              data-wishlist-compare-at-price='${
                firstVariant.compare_at_price ? (firstVariant.compare_at_price / 100).toFixed(2) : ''
              }'>
              <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule="evenodd" clip-rule="evenodd" d="M15.9815 9.12331C13.5 4.50001 3.27574 4.03073 4.0277 13.2251C4.41822 18 16.0301 27.3334 16.0301 27.3334C16.0301 27.3334 27.5858 18 28 13.1722C28.7841 4.0336 18.5 4.50006 15.9815 9.12331Z" stroke="var(--light__ui_element)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
          `;

            const colorOptionsHTML = product.options.includes('Color')
              ? `<div class='product-tile__colors'>${product.options
                  .find((o) => o.name === 'Color')
                  .values.map((color) => `<span>${color}</span>`)
                  .join('')}</div>`
              : '';

            const sizeOptionsHTML = product.options.includes('Size')
              ? `<div class='product-tile__sizes'>${product.options
                  .find((o) => o.name === 'Size')
                  .values.map((size) => `<div class='product-tile__sizes-label'>${size}</div>`)
                  .join('')}</div>`
              : '';

            // Generate tags HTML with proper validation and wrapper
            const tagsHTML =
              Array.isArray(product.tags) && product.tags.some((item) => item.includes('TAG__'))
                ? `<div class="product-tags">
                ${product.tags
                  .map((item) =>
                    item.includes('TAG__')
                      ? `<div class="product-tags__tag">${item.replace(/^TAGS?__/, '')}</div>`
                      : '',
                  )
                  .join('')}
                 </div>`
                : '';

            const productHTML = `
          <div class="swiper-slide">
            <div class="product-tile__container">
              <div class="product-tile__gallery">
                <a target="_blank" href="${product.url}" class="product-tile__gallery-link"></a>
                <div class='swiper-wrapper'>
                  ${mediaHTML}
                </div>
                ${tagsHTML}
                ${wishlistHTML}
                ${colorOptionsHTML}
                ${sizeOptionsHTML}
              </div>
              <div class="product-tile__info">
                <div class="product-tile__meta">
                  <a target="_blank" href="${product.url}" class="product-tile__title">
                    <p>${product.title}</p>
                  </a>
                </div>

                <div class="product-tile__price">
                  ${firstVariant.compare_at_price ? `<del data-compare-at-price>${comparePrice}</del>` : ''}
                  <span data-price>${price}</span>
                </div>

              </div>
            </div>
          </div>
          `;

            container.insertAdjacentHTML('beforeend', productHTML);
          })
          .then(() => {
            container.querySelectorAll('.product-tile__container').forEach((tile) => {
              window.dAgency.initProductTile(tile);
            });

            const carouselContainer = document.querySelector('.recently-viewed__list-container .carousel__container');

            if (carouselContainer && typeof window.dAgency.initCarousel == 'function') {
              window.dAgency.initCarousel(carouselContainer);
            }
          });
      });
    } else {
      section.style.display = 'none';
    }
  }
}

/**
 * Initialize recently viewed tracking on product pages
 * Runs on DOMContentLoaded to ensure page is fully loaded
 */
document.addEventListener('DOMContentLoaded', function () {
  const productContainer = document.querySelector('.product-page__section');
  const cartPageContainer = document.querySelector('.cart-page__container');
  if (productContainer || cartPageContainer) {
    const url = window.location.pathname;
    const productHandle = url.split('/').pop();

    if (productHandle) {
      addProductToRecentlyViewed(productHandle);
    }

    displayRecentlyViewed();
  }
});
